import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Button, Card, EmptyState, ErrorNote, Field, Input, PageHeader, Select, Spinner, statusTone, Table, Textarea } from "../components/ui";
import { cls, fmtDateTime } from "../lib/format";

const TABS = [
  { id: "seed-groups", label: "Seed Groups" },
  { id: "providers", label: "Providers" },
  { id: "system-health", label: "System Health" },
  { id: "logs", label: "Logs" },
  { id: "settings", label: "Settings" },
];

export function AdminPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const tab = location.pathname.split("/admin/")[1] ?? "seed-groups";
  return (
    <div>
      <PageHeader title="Admin" subtitle="System configuration, providers, health, and logs" />
      <div className="mb-6 flex flex-wrap gap-1 border-b border-ink-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => navigate(`/admin/${t.id}`)}
            className={cls(
              "border-b-2 px-4 py-2.5 text-sm font-medium",
              tab === t.id ? "border-brand-600 text-brand-700" : "border-transparent text-ink-500 hover:text-ink-700",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "seed-groups" && <SeedGroups />}
      {tab === "providers" && <Providers />}
      {tab === "system-health" && <SystemHealth />}
      {tab === "logs" && <Logs />}
      {tab === "settings" && <Settings />}
    </div>
  );
}

function SeedGroups() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["seed-groups"], queryFn: () => api<any[]>("/seed-groups") });
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", kind: "group", region: "", country: "", provider: "", accountType: "" });
  const save = useMutation({
    mutationFn: (payload: any) => api("/seed-groups", { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["seed-groups"] }); setModal(false); },
  });
  const del = useMutation({
    mutationFn: (id: string) => api(`/seed-groups/${id}`, { method: "DELETE", body: "{}" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["seed-groups"] }),
  });
  return (
    <>
      <Card className="p-0">
        {isLoading ? <Spinner /> : !data?.length ? (
          <div className="p-6"><EmptyState title="No groups yet" description="Create regions, classes, and provider groups to organize seed accounts." /></div>
        ) : (
          <Table headers={["Name", "Kind", "Region", "Country", "Provider", "Type", "Children", "Actions"]}>
            {data!.map((g) => (
              <tr key={g.id} className="table-row-hover">
                <td className="px-4 py-3 font-medium text-ink-800">{g.name}</td>
                <td className="px-4 py-3 capitalize text-ink-600">{g.kind}</td>
                <td className="px-4 py-3 text-ink-500">{g.region ?? "—"}</td>
                <td className="px-4 py-3 text-ink-500">{g.country ?? "—"}</td>
                <td className="px-4 py-3 text-ink-500">{g.provider ?? "—"}</td>
                <td className="px-4 py-3 capitalize text-ink-500">{g.accountType ?? "—"}</td>
                <td className="px-4 py-3 tabular-nums">{g.children?.length ?? 0}</td>
                <td className="px-4 py-3">
                  <button className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50" onClick={() => { if (confirm(`Delete group ${g.name}?`)) del.mutate(g.id); }}>Delete</button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
      <div className="mt-4"><Button onClick={() => setModal(true)}>+ New Group</Button></div>
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-ink-900/40" onClick={() => setModal(false)} />
          <div className="relative w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="mb-4 text-sm font-semibold">New Seed Group</h3>
            <div className="space-y-3">
              <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="Kind">
                <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                  <option value="region">Region</option>
                  <option value="class">Account Class</option>
                  <option value="group">Provider Group</option>
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Region"><Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} /></Field>
                <Field label="Country"><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Provider filter"><Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="gmail" /></Field>
                <Field label="Account type">
                  <Select value={form.accountType} onChange={(e) => setForm({ ...form, accountType: e.target.value })}>
                    <option value="">Any</option>
                    <option value="professional">Professional</option>
                    <option value="personal">Personal</option>
                  </Select>
                </Field>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" onClick={() => setModal(false)}>Cancel</Button>
                <Button onClick={() => save.mutate(form)} disabled={save.isPending}>Create</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Providers() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["providers"], queryFn: () => api<any[]>("/admin/providers") });
  const { data: oauthClients } = useQuery({ queryKey: ["oauth-clients"], queryFn: () => api<any[]>("/oauth/clients") });
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ type: "spamassassin", name: "", endpoint: "", enabled: true });
  const [oauthModal, setOauthModal] = useState(false);
  const [oauthForm, setOauthForm] = useState({ name: "", clientId: "", clientSecret: "", tenant: "common", redirectUri: "" });
  const save = useMutation({
    mutationFn: (payload: any) => api("/admin/providers", { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["providers"] }); setModal(false); },
  });
  const del = useMutation({ mutationFn: (id: string) => api(`/admin/providers/${id}`, { method: "DELETE", body: "{}" }), onSuccess: () => qc.invalidateQueries({ queryKey: ["providers"] }) });
  const saveOauth = useMutation({
    mutationFn: (payload: any) => api("/oauth/clients", { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["oauth-clients"] }); setOauthModal(false); },
  });
  const delOauth = useMutation({ mutationFn: (id: string) => api(`/oauth/clients/${id}`, { method: "DELETE", body: "{}" }), onSuccess: () => qc.invalidateQueries({ queryKey: ["oauth-clients"] }) });
  return (
    <>
      <Card title="Provider integrations" subtitle="SpamAssassin, DNSBL lists, relays, and API providers">
        {isLoading ? <Spinner /> : !data?.length ? (
          <EmptyState title="No provider configurations" description="Configure integration endpoints here. Credentials are encrypted at rest." />
        ) : (
          <Table headers={["Type", "Name", "Config", "Status", "Enabled", "Actions"]}>
            {data!.map((p) => (
              <tr key={p.id} className="table-row-hover">
                <td className="px-4 py-3"><Badge tone="brand">{p.type}</Badge></td>
                <td className="px-4 py-3 font-medium text-ink-800">{p.name}</td>
                <td className="max-w-[280px] truncate px-4 py-3 font-mono text-xs text-ink-500">{JSON.stringify(p.config)}</td>
                <td className="px-4 py-3"><Badge tone={statusTone(p.status)}>{p.status}</Badge></td>
                <td className="px-4 py-3">{p.enabled ? <Badge tone="success">Enabled</Badge> : <Badge tone="neutral">Disabled</Badge>}</td>
                <td className="px-4 py-3"><button className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50" onClick={() => { if (confirm(`Delete ${p.name}?`)) del.mutate(p.id); }}>Delete</button></td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
      <div className="mt-4"><Button onClick={() => setModal(true)}>+ Add Provider</Button></div>
      <Card className="mt-6" title="OAuth App Registrations (Microsoft)" subtitle="Azure app registration used for XOAUTH2 IMAP/SMTP access — client secret is encrypted at rest">
        {!oauthClients?.length ? (
          <EmptyState title="No OAuth client configured" description="Add your Microsoft Entra/Azure app registration to enable OAuth (XOAUTH2) sign-in for seed and sending accounts." />
        ) : (
          <Table headers={["Name", "Client ID", "Tenant", "Redirect URI", "Enabled", "Actions"]}>
            {oauthClients!.map((c) => (
              <tr key={c.id} className="table-row-hover">
                <td className="px-4 py-3 font-medium text-ink-800">{c.name}</td>
                <td className="px-4 py-3 font-mono text-xs text-ink-600">{c.clientId}</td>
                <td className="px-4 py-3 font-mono text-xs text-ink-500">{c.tenant}</td>
                <td className="max-w-[260px] truncate px-4 py-3 font-mono text-xs text-ink-500">{c.redirectUri}</td>
                <td className="px-4 py-3">{c.enabled ? <Badge tone="success">Enabled</Badge> : <Badge tone="neutral">Disabled</Badge>}</td>
                <td className="px-4 py-3"><button className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50" onClick={() => { if (confirm(`Delete OAuth client ${c.name}?`)) delOauth.mutate(c.id); }}>Delete</button></td>
              </tr>
            ))}
          </Table>
        )}
        <div className="mt-4"><Button onClick={() => { setOauthForm({ name: "", clientId: "", clientSecret: "", tenant: "common", redirectUri: "" }); setOauthModal(true); }}>+ Add OAuth Client</Button></div>
      </Card>
      {oauthModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-ink-900/40" onClick={() => setOauthModal(false)} />
          <div className="relative w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="mb-4 text-sm font-semibold">Microsoft OAuth App Registration</h3>
            <div className="space-y-3">
              <Field label="Name"><Input value={oauthForm.name} onChange={(e) => setOauthForm({ ...oauthForm, name: e.target.value })} placeholder="My Microsoft app" /></Field>
              <Field label="Application (client) ID"><Input value={oauthForm.clientId} onChange={(e) => setOauthForm({ ...oauthForm, clientId: e.target.value })} /></Field>
              <Field label="Client secret"><Input type="password" value={oauthForm.clientSecret} onChange={(e) => setOauthForm({ ...oauthForm, clientSecret: e.target.value })} /></Field>
              <Field label="Tenant"><Input value={oauthForm.tenant} onChange={(e) => setOauthForm({ ...oauthForm, tenant: e.target.value })} placeholder="common" /></Field>
              <Field label="Redirect URI (must match Azure)" hint={`Default: ${window.location.origin}/api/v1/oauth/callback`}>
                <Input value={oauthForm.redirectUri} onChange={(e) => setOauthForm({ ...oauthForm, redirectUri: e.target.value })} placeholder={`${window.location.origin}/api/v1/oauth/callback`} />
              </Field>
              <p className="text-xs text-ink-400">Required API permissions: IMAP.AccessAsUser.All and SMTP.Send (delegated), plus offline_access.</p>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" onClick={() => setOauthModal(false)}>Cancel</Button>
                <Button onClick={() => saveOauth.mutate(oauthForm)} disabled={saveOauth.isPending}>Save</Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-ink-900/40" onClick={() => setModal(false)} />
          <div className="relative w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="mb-4 text-sm font-semibold">Add Provider</h3>
            <div className="space-y-3">
              <Field label="Type">
                <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option value="spamassassin">SpamAssassin</option>
                  <option value="dnsbl">DNSBL</option>
                  <option value="relay">SMTP Relay</option>
                  <option value="api_provider">API Provider</option>
                </Select>
              </Field>
              <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="Endpoint / Config JSON"><Textarea rows={3} value={form.endpoint} onChange={(e) => setForm({ ...form, endpoint: e.target.value })} placeholder='{"url":"http://localhost:783"}' className="font-mono text-xs" /></Field>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" onClick={() => setModal(false)}>Cancel</Button>
                <Button onClick={() => {
                  let config: Record<string, unknown> = {};
                  try { config = form.endpoint ? JSON.parse(form.endpoint) : {}; } catch { config = { endpoint: form.endpoint }; }
                  save.mutate({ type: form.type, name: form.name, config, enabled: form.enabled });
                }} disabled={save.isPending}>Save</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SystemHealth() {
  const { data, isLoading } = useQuery({ queryKey: ["health"], queryFn: () => api<any>("/admin/system-health"), refetchInterval: 15_000 });
  if (isLoading) return <Spinner />;
  if (!data) return <EmptyState title="Health unavailable" />;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <HealthCard title="Seed Accounts" items={[
        { label: "Connected", value: data.seedAccounts.connected, tone: "success" },
        { label: "Disconnected", value: data.seedAccounts.disconnected, tone: "neutral" },
        { label: "Errors", value: data.seedAccounts.errors, tone: "danger" },
      ]} />
      <HealthCard title="Sending Accounts" items={[
        { label: "Connected", value: data.sendingAccounts.connected, tone: "success" },
        { label: "Disconnected", value: data.sendingAccounts.disconnected, tone: "neutral" },
      ]} />
      <HealthCard title="Workers & Queue" items={[
        { label: "Active runs", value: data.workers.active, tone: "brand" },
        { label: "Queued jobs", value: data.queue.queued, tone: "neutral" },
        { label: "Failed jobs", value: data.queue.failed, tone: data.queue.failed > 0 ? "danger" : "success" },
      ]} />
      <HealthCard title="Operations (24h)" items={[
        { label: "IMAP checks", value: data.imapChecks, tone: "neutral" },
        { label: "SMTP sends", value: data.smtpSends, tone: "neutral" },
        { label: "Last scheduler run", value: data.lastCronExecution ? fmtDateTime(data.lastCronExecution) : "—", tone: "neutral" },
      ]} />
    </div>
  );
}

function HealthCard({ title, items }: { title: string; items: { label: string; value: string | number; tone: string }[] }) {
  return (
    <Card title={title}>
      <div className="space-y-3">
        {items.map((i) => (
          <div key={i.label} className="flex items-center justify-between">
            <span className="text-sm text-ink-500">{i.label}</span>
            <Badge tone={(i.tone as any) ?? "neutral"}>{i.value}</Badge>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Logs() {
  const [level, setLevel] = useState("");
  const [category, setCategory] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["logs", level, category],
    queryFn: () => api<{ items: any[] }>(`/admin/logs?level=${level}&category=${category}`),
  });
  return (
    <Card className="p-0">
      <div className="flex flex-wrap items-center gap-3 border-b border-ink-100 p-4">
        <Select value={level} onChange={(e) => setLevel(e.target.value)} className="max-w-[140px]">
          <option value="">All levels</option>
          <option value="debug">debug</option>
          <option value="info">info</option>
          <option value="warn">warn</option>
          <option value="error">error</option>
        </Select>
        <Select value={category} onChange={(e) => setCategory(e.target.value)} className="max-w-[180px]">
          <option value="">All categories</option>
          {["send", "scan", "analyze", "blacklist", "report", "scheduler", "alerts", "import", "runs", "templates"].map((c) => <option key={c} value={c}>{c}</option>)}
        </Select>
      </div>
      {isLoading ? <Spinner /> : !data?.items.length ? (
        <div className="p-6"><EmptyState title="No log entries" /></div>
      ) : (
        <div className="divide-y divide-ink-100">
          {data!.items.map((log) => (
            <div key={log.id} className="flex gap-4 px-5 py-3">
              <Badge tone={statusTone(log.level)}>{log.level}</Badge>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-ink-800">{log.message}</div>
                <div className="mt-0.5 text-xs text-ink-400">{log.category} · {fmtDateTime(log.createdAt)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function Settings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["settings"], queryFn: () => api<Record<string, any>>("/admin/settings") });
  const [form, setForm] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api("/admin/settings", { method: "PATCH", body: JSON.stringify(payload) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings"] }),
  });
  if (isLoading) return <Spinner />;
  return (
    <Card title="Workspace settings">
      <div className="max-w-md space-y-4">
        {["company_name", "scan_delay_seconds", "default_from_name"].map((key) => (
          <Field key={key} label={key.replace(/_/g, " ")}>
            <Input
              value={form[key] ?? String(data?.[key] ?? "")}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              placeholder={String(data?.[key] ?? "")}
            />
          </Field>
        ))}
        <Button onClick={() => save.mutate(form)} disabled={save.isPending}>Save settings</Button>
        <ErrorNote2 />
      </div>
    </Card>
  );
}

function ErrorNote2() {
  return null;
}
